import {
    Component,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Input,
} from '@angular/core';
import { Router } from '@angular/router';
import { ZVideoSafeURL } from '../../machine-infos/ZVideoSafeURL';
import { MachineInfosService } from '@app/components/machine-infos/machine-infos.service';
import { Gallery, GalleryItem, ImageItem, ThumbnailsPosition, ImageSize } from 'ng-gallery';
import { Lightbox } from 'ng-gallery/lightbox';
import { NgClass } from '@angular/common';

@Component({
    selector: 'app-medien',
    templateUrl: './medien.component.html',
    styleUrls: ['./medien.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass]
})
export class MedienComponent implements OnInit, OnChanges, OnDestroy {
    readonly imageGalleryColumnCount = 4;
    private static readonly lightboxGalleryId = 'lightbox';

    /** Irregular flex weights for a less uniform masonry look. */
    private static readonly imageFlexWeights = [7, 2, 5, 3, 8, 2, 4, 6, 3, 5, 1, 6, 4, 2, 7, 3];

    @Input() mode: string;
    @Input() videos: ZVideoSafeURL[];
    @Input() images: string[];
    currentVid: ZVideoSafeURL;

    items: GalleryItem[] = [];

    constructor(
        private readonly machineinfos: MachineInfosService,
        private readonly router: Router,
        private readonly gallery: Gallery,
        private readonly lightbox: Lightbox,
        private readonly cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit() {
        if (!this.mode) {
            this.mode = this.router.url.split('/').pop();
        }

        switch (this.mode) {
            case 'images': {
                this.loadLightboxItems(this.images);
                if (!this.images?.length) {
                    this.machineinfos.images$.subscribe(result => {
                        this.images = result;
                        this.loadLightboxItems(result);
                        this.cdr.markForCheck();
                    });
                }
                break;
            }
            case 'videos': {
                this.currentVid = this.videos?.[0];

                if (!this.videos?.length) {
                    this.machineinfos.videos$.subscribe(
                        result => {
                            this.videos = result;
                            this.currentVid = result[0];
                            this.cdr.markForCheck();
                        });
                }
                break;
            }
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['images']) {
            this.loadLightboxItems(changes['images'].currentValue);
        }
    }

    ngOnDestroy() {
        this.gallery.ref(MedienComponent.lightboxGalleryId).destroy();
    }

    changeCurrentVid(video: ZVideoSafeURL) {
        this.currentVid = video;
    }

    openLightbox(index: number) {
        this.loadLightboxItems(this.images);
        this.lightbox.open(index, MedienComponent.lightboxGalleryId, {
            panelClass: 'fullscreen'
        });
    }

    /** Flat index in `images` for a tile in the masonry column layout. */
    getImageIndex(columnIndex: number, rowInColumn: number): number {
        return columnIndex + rowInColumn * this.imageGalleryColumnCount;
    }

    /** Distributes images across columns so each column fills the gallery rectangle. */
    get imageColumns(): string[][] {
        const columns: string[][] = Array.from(
            { length: this.imageGalleryColumnCount },
            () => []
        );
        (this.images ?? []).forEach((img, index) =>
            columns[index % this.imageGalleryColumnCount].push(img)
        );
        return columns.filter(column => column.length > 0);
    }

    /** Picks a flex-grow weight from a shuffled pattern (stable per tile position). */
    getImageFlexGrow(columnIndex: number, rowInColumn: number): number {
        const globalIndex = this.getImageIndex(columnIndex, rowInColumn);
        const patternIndex =
            (globalIndex * 7 + columnIndex * 3 + rowInColumn * 11) %
            MedienComponent.imageFlexWeights.length;
        return MedienComponent.imageFlexWeights[patternIndex];
    }

    private loadLightboxItems(images: string[] | null | undefined) {
        this.items = (images ?? []).map(src => new ImageItem({ src, thumb: src }));

        const lightboxRef = this.gallery.ref(MedienComponent.lightboxGalleryId);
        lightboxRef.setConfig({
            imageSize: ImageSize.Contain,
            thumbPosition: ThumbnailsPosition.Top
        });
        lightboxRef.load(this.items);
    }
}
